'use client'

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import type { MouseEvent } from 'react'
import { motion } from 'framer-motion'
import type { ControllerRef } from 'yet-another-react-lightbox'
import type { Slide } from 'yet-another-react-lightbox'
import BlurredLightbox from '@/components/ui/BlurredLightbox'
import LightboxImageSlide from '@/components/ui/LightboxImageSlide'
import LightboxBeforeAfterSlide from '@/components/ui/LightboxBeforeAfterSlide'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { getOptimizedImageProps } from '@inoo-ch/payload-image-optimizer/frontend'
import type { Media as MediaType } from '@/payload-types'
import type {
  InspirationGalleryItem,
  InspirationGalleryBeforeAfterSide,
} from '@/types/inspiration-gallery'

type LightboxSlide = {
  src: string
  alt: string
  width?: number
  height?: number
  blurDataURL?: string
  resource?: MediaType | null
  variant?: InspirationGalleryItem['media']['variant']
  before?: InspirationGalleryBeforeAfterSide & { blurDataURL?: string }
  after?: InspirationGalleryBeforeAfterSide & { blurDataURL?: string }
  defaultSplit?: number
}

interface _ProjectImageSource {
  id: string
  title: string
  url: string
  alt: string
  projectSlug: string
}
import { filterInspirationByProduct } from '@/utilities/filterInspirationByProduct'
import { track } from '@/utilities/analytics'
import InspirationCard from '@/components/InspirationCard/InspirationCard'
import FilterButton from '@/components/ui/FilterButton'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import Link from 'next/link'

interface InspirationListClientProps {
  items: InspirationGalleryItem[]
  products: Array<{ id: string; title: string; slug: string }>
}

/**
 * Client Component: Inspiration List
 * Full gallery with filters, pagination, lightbox, and before/after modal.
 * Used on the dedicated Inspiration page.
 */
export default function InspirationListClient({
  items,
  products,
}: InspirationListClientProps) {
  const batchSize = 16
  const [activeFilter, setActiveFilter] = useState('all')
  const [visibleCount, setVisibleCount] = useState(batchSize)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [lightboxSlides, setLightboxSlides] = useState<LightboxSlide[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const controllerRef = useRef<ControllerRef | null>(null)
  const isInitialMount = useRef(true)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  // Track initial mount to prevent layout animations on hydration
  useEffect(() => {
    // After first render, allow layout animations for filter changes and load more
    isInitialMount.current = false
  }, [])

  // Filter items based on active filter (memoized)
  const filteredItems = useMemo(
    () => filterInspirationByProduct(items, activeFilter),
    [items, activeFilter],
  )

  // Get visible items based on current count (memoized)
  const visibleItems = useMemo(
    () => filteredItems.slice(0, visibleCount),
    [filteredItems, visibleCount],
  )

  // Check if there are more items to load
  const hasMoreItems = visibleCount < filteredItems.length

  // Reset visible count when filter changes
  useEffect(() => {
    setVisibleCount(batchSize)
  }, [activeFilter])

  // Convert inspiration item to lightbox slide (memoized callback)
  const mapItemToSlide = useCallback((item: InspirationGalleryItem): LightboxSlide => {
    const getBlur = (resource?: MediaType | null) => {
      if (!resource) return undefined
      return getOptimizedImageProps(resource as any).blurDataURL
    }

    if (item.media.variant === 'beforeAfter') {
      return {
        variant: 'beforeAfter',
        src: item.media.after?.src || '',
        alt: item.media.after?.alt || '',
        before: item.media.before
          ? { ...item.media.before, blurDataURL: getBlur(item.media.before.resource) }
          : item.media.before,
        after: item.media.after
          ? { ...item.media.after, blurDataURL: getBlur(item.media.after.resource) }
          : item.media.after,
        defaultSplit: item.media.defaultSplit || 0.5,
      }
    }
    return {
      variant: 'image',
      src: item.media.image?.src || '',
      alt: item.media.image?.alt || '',
      width: item.media.image?.width,
      height: item.media.image?.height,
      blurDataURL: getBlur(item.media.image?.resource),
      resource: item.media.image?.resource ?? null,
    }
  }, [])

  // Handle lightbox open (memoized callback)
  const handleLightboxOpen = useCallback((item: InspirationGalleryItem) => {
    const slides = filteredItems.map(mapItemToSlide)
    const itemIndex = filteredItems.findIndex((i) => i.id === item.id)

    setLightboxSlides(slides)
    setLightboxIndex(itemIndex)
    setLightboxOpen(true)
    track('gallery_image_opened', {
      gallery_block: 'inspiration',
      image_index: itemIndex >= 0 ? itemIndex : undefined,
      filter_value: activeFilter,
    })
  }, [filteredItems, mapItemToSlide, activeFilter])

  const handleFilterChange = useCallback((value: string) => {
    setActiveFilter(value)
    track('inspiration_filter_applied', { filter_value: value })
  }, [])

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting && hasMoreItems && !isLoading) {
          setIsLoading(true)
          // Brief delay so the loading animation is visible
          setTimeout(() => {
            setVisibleCount((prev) => prev + batchSize)
            setIsLoading(false)
          }, 400)
        }
      },
      { rootMargin: '200px' },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMoreItems, isLoading])

  return (
    <div>
      {/* Topic Filters */}
      {products.length > 0 && (
        <MotionFadeIn>
          <div className="mb-12">
            {/* Desktop: button row */}
            <div className="hidden lg:flex flex-wrap gap-4 md:gap-6 lg:gap-8 items-start justify-center">
              {products.map((topic) => (
                <FilterButton
                  key={topic.slug}
                  label={topic.title}
                  isActive={activeFilter === topic.slug}
                  onClick={() => handleFilterChange(topic.slug)}
                />
              ))}
            </div>

            {/* Mobile & Tablet: native select */}
            <div className="lg:hidden relative">
              <label htmlFor="inspiration-filter" className="sr-only">
                Kategorie filtern
              </label>
              <select
                id="inspiration-filter"
                value={activeFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="w-full appearance-none bg-transparent border-b border-text-primary/30 py-3 pr-10 pl-0 font-ui text-base uppercase text-text-primary focus:outline-none focus:border-text-primary transition-colors"
              >
                {products.map((topic) => (
                  <option key={topic.slug} value={topic.slug}>
                    {topic.title}
                  </option>
                ))}
              </select>
              <ChevronRight
                aria-hidden="true"
                className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-5 w-5 rotate-90 text-text-primary/60"
              />
            </div>
          </div>
        </MotionFadeIn>
      )}

      {/* Inspiration Grid */}
      <div className="mb-12">
        {visibleItems.length > 0 ? (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            layout={!isInitialMount.current}
          >
            {visibleItems.map((item) => (
              <motion.div
                key={item.id}
                layout={!isInitialMount.current}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="w-full"
              >
                <MotionFadeIn>
                  <InspirationCard item={item} onGalleryOpen={handleLightboxOpen} />
                </MotionFadeIn>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <MotionFadeIn>
            <div className="text-center py-16">
              <p className="text-text-secondary">
                Keine Inspirations-Einträge für diese Kategorie gefunden.
              </p>
            </div>
          </MotionFadeIn>
        )}
      </div>

      {/* Infinite scroll sentinel + loading indicator */}
      {hasMoreItems && visibleItems.length > 0 && (
        <div ref={sentinelRef} className="flex justify-center py-12">
          {isLoading && (
            <div className="flex items-center gap-3">
              <div className="grid grid-cols-2 gap-1.5">
                <span className="block h-2.5 w-2.5 rounded-sm bg-brand animate-pulse [animation-delay:0ms]" />
                <span className="block h-2.5 w-2.5 rounded-sm bg-brand/60 animate-pulse [animation-delay:150ms]" />
                <span className="block h-2.5 w-2.5 rounded-sm bg-brand/60 animate-pulse [animation-delay:300ms]" />
                <span className="block h-2.5 w-2.5 rounded-sm bg-brand animate-pulse [animation-delay:150ms]" />
              </div>
              <span className="text-sm text-text-secondary font-ui">Lade weitere Bilder...</span>
            </div>
          )}
        </div>
      )}

      {/* Lightbox Gallery */}
      <BlurredLightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxSlides as unknown as Slide[]}
        carousel={{ finite: false }}
        controller={{
          ref: controllerRef,
          disableSwipeNavigation:
            lightboxSlides[lightboxIndex]?.variant === 'beforeAfter',
        }}
        on={{
          view: ({ index }) => {
            setLightboxIndex(index)
          },
        }}
        styles={{
          navigationPrev: { display: 'none' },
          navigationNext: { display: 'none' },
        }}
        render={{
          controls: () => {
            if (!lightboxOpen || lightboxSlides.length <= 1) {
              return null
            }

            const handlePrev = (event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()
              controllerRef.current?.prev()
            }

            const handleNext = (event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()
              controllerRef.current?.next()
            }

            return (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-4 sm:px-6">
                <button
                  type="button"
                  aria-label="Vorheriges Bild"
                  onClick={handlePrev}
                  className="pointer-events-auto inline-flex h-12 w-12 items-center justify-center bg-brand text-white shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  aria-label="Nächstes Bild"
                  onClick={handleNext}
                  className="pointer-events-auto inline-flex h-12 w-12 items-center justify-center bg-brand text-white shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
            )
          },
          slide: ({ slide, rect }) => {
            const lightboxSlide = slide as LightboxSlide
            const currentItem = filteredItems[lightboxIndex]
            const isProjectImage =
              currentItem?.source === 'project' &&
              currentItem?.sourceMetadata &&
              'projectSlug' in currentItem.sourceMetadata

            if (
              lightboxSlide.variant === 'beforeAfter' &&
              lightboxSlide.before &&
              lightboxSlide.after
            ) {
              return (
                <LightboxBeforeAfterSlide
                  rect={rect}
                  before={{
                    src: lightboxSlide.before.src,
                    alt: lightboxSlide.before.alt,
                    width: lightboxSlide.before.width,
                    height: lightboxSlide.before.height,
                    blurDataURL: lightboxSlide.before.blurDataURL,
                    resource: lightboxSlide.before.resource,
                  }}
                  after={{
                    src: lightboxSlide.after.src,
                    alt: lightboxSlide.after.alt,
                    width: lightboxSlide.after.width,
                    height: lightboxSlide.after.height,
                    blurDataURL: lightboxSlide.after.blurDataURL,
                    resource: lightboxSlide.after.resource,
                  }}
                  defaultPosition={(lightboxSlide.defaultSplit ?? 0.5) * 100}
                  leftLabel={lightboxSlide.before.label ?? 'VORHER'}
                  rightLabel={lightboxSlide.after.label ?? 'NACHHER'}
                />
              )
            }

            return (
              <LightboxImageSlide
                src={lightboxSlide.src}
                alt={lightboxSlide.alt}
                resource={lightboxSlide.resource}
                intrinsicWidth={lightboxSlide.width}
                intrinsicHeight={lightboxSlide.height}
                rect={rect}
                blurDataURL={lightboxSlide.blurDataURL}
                belowImage={
                  isProjectImage ? (
                    <Link
                      href={`/projekte/${(currentItem.sourceMetadata as any)?.projectSlug || ''}`}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-brand hover:bg-brand/90 text-white px-6 py-3 font-metropolis font-medium uppercase transition-colors duration-200"
                    >
                      Projekt ansehen
                    </Link>
                  ) : null
                }
              />
            )
          },
        }}
      />

    </div>
  )
}
